import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const MEAL_COLORS_HEX = { breakfast: '#f59e0b', lunch: '#10b981', dinner: '#6366f1' };
const CAT_COLORS_HEX = { student: '#3b82f6', resident: '#8b5cf6', staff: '#10b981', visitor: '#f59e0b' };

export default function Analytics({ isAdmin }) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, approved: 0, denied: 0, byMeal: [], byCategory: [], byDay: [], topCardholders: [] });
  const [filterDateFrom, setFilterDateFrom] = useState(() => {
    const d = new Date(); d?.setDate(d?.getDate() - 30); return d?.toISOString()?.split('T')?.[0];
  });
  const [filterDateTo, setFilterDateTo] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [filterMeal, setFilterMeal] = useState('all');
  const [filterCat, setFilterCat] = useState('all');
  const [scanLogs, setScanLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [mealTimes, setMealTimes] = useState([]);
  const [savingTimes, setSavingTimes] = useState(false);

  useEffect(() => { fetchAnalytics(); fetchMealTimes(); }, [filterDateFrom, filterDateTo, filterMeal, filterCat]);

  const fetchMealTimes = async () => {
    const { data } = await supabase?.from('restaurant_meal_times')?.select('*')?.order('start_time');
    setMealTimes(data || []);
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    setLogsLoading(true);
    try {
      let query = supabase?.from('restaurant_scan_logs')?.select('*, restaurant_cardholders(full_name, category, building, department)')?.gte('scan_time', `${filterDateFrom}T00:00:00`)?.lte('scan_time', `${filterDateTo}T23:59:59`)?.order('scan_time', { ascending: false });

      if (filterMeal !== 'all') query = query?.eq('meal_type', filterMeal);
      if (filterCat !== 'all') query = query?.eq('restaurant_cardholders.category', filterCat);

      const { data: logs } = await query;
      const allLogs = logs || [];
      setScanLogs(allLogs?.slice(0, 50));

      const approved = allLogs?.filter(l => l?.scan_result === 'approved');
      const denied = allLogs?.filter(l => l?.scan_result !== 'approved');

      // By meal
      const byMeal = ['breakfast', 'lunch', 'dinner']?.map(m => ({
        name: m?.charAt(0)?.toUpperCase() + m?.slice(1),
        approved: approved?.filter(l => l?.meal_type === m)?.length,
        denied: denied?.filter(l => l?.meal_type === m)?.length,
      }));

      // By category
      const catMap = {};
      approved?.forEach(l => {
        const cat = l?.restaurant_cardholders?.category || 'unknown';
        catMap[cat] = (catMap?.[cat] || 0) + 1;
      });
      const byCategory = Object.entries(catMap)?.map(([name, value]) => ({ name: name?.charAt(0)?.toUpperCase() + name?.slice(1), value }));

      // By day (last 14 days)
      const dayMap = {};
      allLogs?.forEach(l => {
        const day = l?.scan_time?.split('T')?.[0];
        if (!dayMap?.[day]) dayMap[day] = { day, approved: 0, denied: 0 };
        if (l?.scan_result === 'approved') dayMap[day].approved++;
        else dayMap[day].denied++;
      });
      const byDay = Object.values(dayMap)?.sort((a, b) => a?.day?.localeCompare(b?.day))?.slice(-14);

      // Top cardholders
      const chMap = {};
      approved?.forEach(l => {
        const name = l?.restaurant_cardholders?.full_name || 'Unknown';
        chMap[name] = (chMap?.[name] || 0) + 1;
      });
      const topCardholders = Object.entries(chMap)?.sort((a, b) => b?.[1] - a?.[1])?.slice(0, 5)?.map(([name, count]) => ({ name, count }));

      setStats({ total: allLogs?.length, approved: approved?.length, denied: denied?.length, byMeal, byCategory, byDay, topCardholders });
    } catch (e) { console.error(e); }
    finally { setLoading(false); setLogsLoading(false); }
  };

  const saveMealTimes = async () => {
    setSavingTimes(true);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      for (const mt of mealTimes) {
        await supabase?.from('restaurant_meal_times')?.update({
          start_time: mt?.start_time, end_time: mt?.end_time, is_active: mt?.is_active, updated_by: user?.id
        })?.eq('id', mt?.id);
      }
    } catch (e) { console.error(e); }
    finally { setSavingTimes(false); }
  };

  const scanResultLabels = { approved: 'Approved', denied_duplicate: 'Duplicate', denied_expired: 'Expired', denied_no_eligibility: 'Not Eligible', denied_wrong_time: 'Wrong Time' };
  const scanResultColors = { approved: 'text-emerald-600 bg-emerald-50', denied_duplicate: 'text-orange-600 bg-orange-50', denied_expired: 'text-red-600 bg-red-50', denied_no_eligibility: 'text-red-600 bg-red-50', denied_wrong_time: 'text-amber-600 bg-amber-50' };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">From</label>
            <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e?.target?.value)}
              className="border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">To</label>
            <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e?.target?.value)}
              className="border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Meal</label>
            <select value={filterMeal} onChange={e => setFilterMeal(e?.target?.value)}
              className="border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30">
              <option value="all">All Meals</option>
              {['breakfast', 'lunch', 'dinner']?.map(m => <option key={m} value={m}>{m?.charAt(0)?.toUpperCase() + m?.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Category</label>
            <select value={filterCat} onChange={e => setFilterCat(e?.target?.value)}
              className="border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30">
              <option value="all">All Categories</option>
              {['student', 'resident', 'staff', 'visitor']?.map(c => <option key={c} value={c}>{c?.charAt(0)?.toUpperCase() + c?.slice(1)}</option>)}
            </select>
          </div>
        </div>
      </div>
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Scans', value: stats?.total, icon: 'Scan', color: 'primary' },
          { label: 'Meals Served', value: stats?.approved, icon: 'CheckCircle2', color: 'emerald' },
          { label: 'Denied Scans', value: stats?.denied, icon: 'XCircle', color: 'red' },
          { label: 'Success Rate', value: stats?.total > 0 ? `${Math.round((stats?.approved / stats?.total) * 100)}%` : '—', icon: 'TrendingUp', color: 'amber' },
        ]?.map(kpi => (
          <div key={kpi?.label} className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name={kpi?.icon} size={16} className={`text-${kpi?.color === 'primary' ? 'primary' : kpi?.color + '-500'}`} />
              <span className="text-xs text-muted-foreground">{kpi?.label}</span>
            </div>
            <div className="text-2xl font-black text-foreground">{loading ? '—' : kpi?.value}</div>
          </div>
        ))}
      </div>
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-semibold text-foreground mb-4 text-sm">Meals by Type</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={stats?.byMeal}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="approved" fill="#10b981" name="Approved" radius={[4, 4, 0, 0]} />
              <Bar dataKey="denied" fill="#f87171" name="Denied" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-semibold text-foreground mb-4 text-sm">Meals by Category</h3>
          {stats?.byCategory?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={stats?.byCategory} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`} labelLine={false}>
                  {stats?.byCategory?.map((entry, i) => (
                    <Cell key={i} fill={Object.values(CAT_COLORS_HEX)?.[i % 4]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-48 text-sm text-muted-foreground">No data for selected period</div>
          )}
        </div>

        <div className="bg-card border border-border rounded-xl p-5 lg:col-span-2">
          <h3 className="font-semibold text-foreground mb-4 text-sm">Daily Scan Activity</h3>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={stats?.byDay}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="approved" fill="#10b981" name="Approved" stackId="a" />
              <Bar dataKey="denied" fill="#f87171" name="Denied" stackId="a" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Meal Time Configuration (Admin only) */}
      {isAdmin && (
        <div className="bg-card border border-border rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              <Icon name="Clock" size={16} className="text-primary" />
              Meal Time Configuration
            </h3>
            <button onClick={saveMealTimes} disabled={savingTimes}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold disabled:opacity-50 hover:opacity-90 transition-all">
              {savingTimes ? <Icon name="Loader2" size={12} className="animate-spin" /> : <Icon name="Save" size={12} />}
              Save Times
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {mealTimes?.map((mt, i) => (
              <div key={mt?.id} className="border border-border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-semibold capitalize text-foreground">{mt?.meal_type}</span>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={mt?.is_active} onChange={e => setMealTimes(prev => prev?.map((x, j) => j === i ? { ...x, is_active: e?.target?.checked } : x))}
                      className="rounded border-border text-primary focus:ring-primary/30" />
                    <span className="text-xs text-muted-foreground">Active</span>
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Start</label>
                    <input type="time" value={mt?.start_time?.slice(0, 5)} onChange={e => setMealTimes(prev => prev?.map((x, j) => j === i ? { ...x, start_time: e?.target?.value } : x))}
                      className="w-full border border-border rounded-lg px-2 py-1.5 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">End</label>
                    <input type="time" value={mt?.end_time?.slice(0, 5)} onChange={e => setMealTimes(prev => prev?.map((x, j) => j === i ? { ...x, end_time: e?.target?.value } : x))}
                      className="w-full border border-border rounded-lg px-2 py-1.5 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Scan Log Table */}
      <div className="bg-card border border-border rounded-xl p-5 space-y-3">
        <h3 className="font-semibold text-foreground flex items-center gap-2">
          <Icon name="ClipboardList" size={16} className="text-primary" />
          Scan Activity Log
        </h3>
        {logsLoading ? (
          <div className="flex items-center justify-center py-8"><Icon name="Loader2" size={20} className="animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">Time</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">Name</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">Category</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">Meal</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">Result</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {scanLogs?.map(s => (
                  <tr key={s?.id} className="hover:bg-muted/30">
                    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{new Date(s.scan_time)?.toLocaleString()}</td>
                    <td className="px-3 py-2 font-medium text-foreground">{s?.restaurant_cardholders?.full_name || '—'}</td>
                    <td className="px-3 py-2 text-xs capitalize text-muted-foreground">{s?.restaurant_cardholders?.category || '—'}</td>
                    <td className="px-3 py-2 text-xs capitalize text-muted-foreground">{s?.meal_type || '—'}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${scanResultColors?.[s?.scan_result]}`}>{scanResultLabels?.[s?.scan_result]}</span>
                    </td>
                  </tr>
                ))}
                {scanLogs?.length === 0 && (
                  <tr><td colSpan={5} className="py-8 text-center text-sm text-muted-foreground">No scan logs for selected period</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
